import { useEffect } from "react";

interface ProductVariationForSeo {
  price: number;
  stock?: number | null;
  name?: string;
}

interface ProductJsonLd {
  name: string;
  description?: string;
  image?: string | string[]; // Support single image or array of images
  price: number;
  currency?: string;
  brand?: string;
  category?: string;
  sku?: string;
  availability?: "InStock" | "OutOfStock" | "PreOrder";
  condition?: "NewCondition" | "UsedCondition" | "RefurbishedCondition";
  variations?: ProductVariationForSeo[]; // For AggregateOffer
}

interface ArticleJsonLd {
  headline: string;
  description?: string;
  image?: string;
  author: string;
  datePublished: string;
  dateModified?: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "product" | "article";
  noIndex?: boolean;
  product?: ProductJsonLd;
  article?: ArticleJsonLd;
  breadcrumbs?: BreadcrumbItem[];
}

const SITE_NAME = "Gordon Store";
const SITE_URL = "https://gordonstore.cr";
const DEFAULT_DESCRIPTION = "Tu tienda de productos premium para mascotas en Costa Rica. Alimentos, juguetes, accesorios y más para perros y gatos.";

export function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  image,
  url,
  type = "website",
  noIndex = false,
  product,
  article,
  breadcrumbs,
}: SEOHeadProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const currentUrl = url || (typeof window !== "undefined" ? window.location.href : SITE_URL);
  const fullImageUrl = image?.startsWith("http") ? image : image ? `${SITE_URL}${image}` : undefined;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to update or create meta tag
    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (content) {
        if (!element) {
          element = document.createElement("meta");
          element.setAttribute(attribute, name);
          document.head.appendChild(element);
        }
        element.setAttribute("content", content);
      } else if (element) {
        element.remove();
      }
    };

    // Helper to manage JSON-LD scripts
    const updateJsonLd = (id: string, data: object | null) => {
      let script = document.querySelector(`script[data-json-ld="${id}"]`);
      
      if (data) {
        if (!script) {
          script = document.createElement("script");
          script.setAttribute("type", "application/ld+json");
          script.setAttribute("data-json-ld", id);
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(data);
      } else if (script) {
        script.remove();
      }
    };

    // Standard meta tags
    updateMeta("description", description);
    if (keywords) {
      updateMeta("keywords", keywords);
    }

    // Robots
    if (noIndex) {
      updateMeta("robots", "noindex, nofollow");
    } else {
      updateMeta("robots", "index, follow");
    }

    // Open Graph tags
    updateMeta("og:title", fullTitle, true);
    updateMeta("og:description", description, true);
    updateMeta("og:type", type === "product" ? "product" : type, true);
    updateMeta("og:url", currentUrl, true);
    updateMeta("og:site_name", SITE_NAME, true);
    updateMeta("og:locale", "es_CR", true);
    if (fullImageUrl) {
      updateMeta("og:image", fullImageUrl, true);
    }

    // Twitter Card tags
    updateMeta("twitter:card", fullImageUrl ? "summary_large_image" : "summary");
    updateMeta("twitter:title", fullTitle);
    updateMeta("twitter:description", description);
    if (fullImageUrl) {
      updateMeta("twitter:image", fullImageUrl);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (currentUrl) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", currentUrl);
    }

    // Product JSON-LD with advanced features
    if (product) {
      // Handle multiple images
      const productImages = Array.isArray(product.image) 
        ? product.image 
        : product.image 
          ? [product.image] 
          : fullImageUrl 
            ? [fullImageUrl] 
            : undefined;

      // Determine availability based on variations stock or default
      let availability = product.availability || "InStock";
      if (product.variations && product.variations.length > 0) {
        const totalStock = product.variations.reduce((sum, v) => sum + (v.stock ?? 0), 0);
        availability = totalStock > 0 ? "InStock" : "OutOfStock";
      }

      // Build offers - use AggregateOffer if multiple variations with different prices
      let offers: object;
      if (product.variations && product.variations.length > 1) {
        const prices = product.variations.map(v => v.price);
        const uniquePrices = [...new Set(prices)];
        
        if (uniquePrices.length > 1) {
          // Multiple different prices - use AggregateOffer
          offers = {
            "@type": "AggregateOffer",
            "lowPrice": Math.min(...prices),
            "highPrice": Math.max(...prices),
            "priceCurrency": product.currency || "CRC",
            "offerCount": product.variations.length,
            "availability": `https://schema.org/${availability}`,
            "seller": {
              "@type": "Organization",
              "name": SITE_NAME
            }
          };
        } else {
          // Same price across variations - use single Offer
          offers = {
            "@type": "Offer",
            "price": product.price,
            "priceCurrency": product.currency || "CRC",
            "availability": `https://schema.org/${availability}`,
            "seller": {
              "@type": "Organization",
              "name": SITE_NAME
            }
          };
        }
      } else {
        // Single variation or no variations - use single Offer
        offers = {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": product.currency || "CRC",
          "availability": `https://schema.org/${availability}`,
          "seller": {
            "@type": "Organization",
            "name": SITE_NAME
          }
        };
      }

      const productJsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": product.description || description,
        "image": productImages,
        "brand": product.brand ? {
          "@type": "Brand",
          "name": product.brand
        } : undefined,
        "category": product.category,
        "sku": product.sku,
        "itemCondition": `https://schema.org/${product.condition || "NewCondition"}`,
        "offers": offers
      };
      updateJsonLd("product", productJsonLd);
    } else {
      updateJsonLd("product", null);
    }

    // Article JSON-LD
    if (article) {
      const articleJsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.headline,
        "description": article.description || description,
        "image": article.image || fullImageUrl,
        "author": {
          "@type": "Person",
          "name": article.author
        },
        "publisher": {
          "@type": "Organization",
          "name": SITE_NAME,
          "logo": {
            "@type": "ImageObject",
            "url": `${SITE_URL}/logo.svg`
          }
        },
        "datePublished": article.datePublished,
        "dateModified": article.dateModified || article.datePublished,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": currentUrl
        }
      };
      updateJsonLd("article", articleJsonLd);
    } else {
      updateJsonLd("article", null);
    }

    // Breadcrumb JSON-LD
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`
        }))
      };
      updateJsonLd("breadcrumb", breadcrumbJsonLd);
    } else {
      updateJsonLd("breadcrumb", null);
    }

    // Cleanup function - reset to defaults when component unmounts
    return () => {
      document.title = SITE_NAME;
      updateJsonLd("product", null);
      updateJsonLd("article", null);
      updateJsonLd("breadcrumb", null);
    };
  }, [fullTitle, description, keywords, fullImageUrl, currentUrl, type, noIndex, product, article, breadcrumbs]);

  return null;
}

export default SEOHead;
